import {useEffect, useState} from "react";
import ApiService from "../../../core/service/ApiService";
import {Button, Divider, Dropdown, Input, Select, Table, Typography, Menu, Space} from "antd";
import {t} from "i18next";
import FileUtils from "../../../core/utils/file-utils";
import Helper from "../../../core/Helper";
import {useUser} from "../../../core/context/UserContext";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import {useModal} from "../../../core/context/ModalContext";
import AppointmentFileEdit from "../AppointmentFileEdit";
import {EditOutlined} from "@ant-design/icons";

export const FileTypes = [
    {id: 1, name: t('Normal')},
    {id: 2, name: t('Patient Result')},
    {id: 3, name: t('Invoice')},
]

export default function AppointmentFiles({appointmentId}) {
    const [files, setFiles] = useState([]);
    const [lastFiles, setLastFiles] = useState([]);
    const [uploadFiles, setUploadFiles] = useState([])
    const [description, setDescription] = useState('')
    const {user} = useUser()
    const {openModal, closeModal} = useModal()

    const getFiles = async () => {
        await Promise.all([
            ApiService.Appointment.GetFiles(appointmentId).then(res => {
                if (res.success) {
                    setFiles(res.data)
                }
            }),
            ApiService.Appointment.GetLastFiles(appointmentId).then(res => {
                if (res.success) {
                    setLastFiles(res.data)
                }
            })
        ])
    }

    useEffect(() => {
        getFiles()
    }, []);

    return (
        <>
            <Space>
                <Button
                    size={'small'}
                    onClick={async () => {
                        const file = await FileUtils.getFileAsBase64({})
                        setUploadFiles([...uploadFiles, {...file, fileType: 1}])
                    }}
                >
                    {t('Select File')}
                </Button>
                <Button
                    size={'small'}
                    type={'primary'}
                    onClick={async () => {
                        for (const uploadFile of uploadFiles) {
                            const res = await ApiService.Appointment.UploadFile(appointmentId, {
                                originalFileName: uploadFile.fileName,
                                description: uploadFile.description,
                                base64Data: uploadFile.data,
                                fileType: uploadFile.fileType,
                            })
                        }

                        await getFiles()
                        setUploadFiles([])
                    }}
                >
                    {t('Upload')}
                </Button>
            </Space>

            {
                uploadFiles?.length > 0 && (
                    <table style={{marginTop: 16}}>
                        <tbody>
                        <tr>
                            <td>
                                <Typography.Title level={5}>
                                    {t('Waiting for file upload')}
                                </Typography.Title>
                            </td>
                        </tr>
                        {uploadFiles.map((file, idx) => (
                            <tr key={`${file.fileName}_${idx}`}>
                                <td>
                                    <Input value={file?.fileName} size={'small'}/>
                                </td>
                                <td>
                                    <div style={{width: 400}}>
                                        <Input
                                            placeholder={t('Description')}
                                            value={file?.description}
                                            size={'small'}
                                            onChange={(e) => {
                                                const index = uploadFiles.findIndex(x => x.fileName === file.fileName);
                                                if (index > -1) {
                                                    uploadFiles[index].description = e.target.value;
                                                    setUploadFiles([...uploadFiles])
                                                }
                                            }}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        <Dropdown
                                            overlay={(
                                                <Menu
                                                    onClick={({key}) => {
                                                        const index = uploadFiles.findIndex(x => x.fileName === file.fileName);
                                                        if (index > -1) {
                                                            uploadFiles[index].description = key;
                                                            setUploadFiles([...uploadFiles]);
                                                        }
                                                    }}
                                                >
                                                    {
                                                        user?.fileDescriptions?.map(x => (
                                                            <Menu.Item key={x.text}>
                                                                <div style={{width: 300, wordWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                                                    {x.text}
                                                                </div>
                                                            </Menu.Item>
                                                        ))
                                                    }
                                                </Menu>
                                            )}
                                            trigger={['click']}>
                                            <Button size={'small'}>
                                                {t('Description')}
                                            </Button>
                                        </Dropdown>
                                        <Select
                                            style={{width: 200}}
                                            size={'small'}
                                            value={file?.fileType}
                                            onChange={(e) => {
                                                const index = uploadFiles.findIndex(x => x.fileName === file.fileName);
                                                if (index > -1) {
                                                    uploadFiles[index].fileType = e;
                                                    setUploadFiles([...uploadFiles])
                                                }
                                            }}
                                        >
                                            {
                                                FileTypes.map(x => (
                                                    <Select.Option key={x.id} value={x.id}>
                                                        {x.name}
                                                    </Select.Option>
                                                ))
                                            }
                                        </Select>
                                    </div>
                                </td>
                                <td>
                                    <Button
                                        size={'small'}
                                        type={'primary'}
                                        danger={true}
                                        onClick={() => {
                                            setUploadFiles([...uploadFiles.filter(x => x.fileName !== file.fileName)])
                                        }}
                                    >
                                        {t('Delete')}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )
            }

            <Typography.Title level={4} style={{marginTop: 16}}>
                {t('Uploaded Files')}
            </Typography.Title>
            <Table
                size={'small'}
                pagination={false}
                dataSource={files}
                scroll={{x: 800}}
                columns={[
                    {dataIndex: 'createdDate', key: 'createdDate', title: t('Date'), render: (text) => new Date(text).toLocaleString(), width: 150},
                    {
                        dataIndex: 'originalFileName',
                        key: 'originalFileName',
                        title: t('File'),
                        render: (text, row) => (
                            <div style={{wordWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                {text} {row?.isDeleted && (<Typography.Text type={'danger'}>X</Typography.Text>)}
                            </div>
                        )
                    },
                    {
                        dataIndex: 'description',
                        key: 'description',
                        title: t('Description'),
                        width: 400,
                        render: (text) => (
                            <div style={{width: 400, wordWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                {text}
                            </div>
                        )
                    },
                    {
                        dataIndex: 'fileType',
                        key: 'fileType',
                        title: t('Type'),
                        width: 150,
                        render: (text) => (
                            <div style={{width: 150, wordWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                {FileTypes.find(x => x.id === text)?.name}
                            </div>
                        )
                    },
                    {
                        dataIndex: '#',
                        key: '#',
                        title: '#',
                        width: 175,
                        render: (text, row) => {
                            return (
                                <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                    <Button
                                        size={'small'}
                                        onClick={async () => {
                                            const res = await ApiService.Appointment.DownloadFile(appointmentId, row.id)
                                            if (res.success) {
                                                window.open(res.data, '_blank')
                                            }
                                        }}
                                    >
                                        {t('Open')}
                                    </Button>
                                    <Button
                                        size={'small'}
                                        onClick={async () => {
                                            const fileUrl = await ApiService.Appointment.GetFileUrl(appointmentId, row.id)
                                            const link = document.createElement('a');
                                            link.href = fileUrl;
                                            link.download = row.originalFileName;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                    >
                                        {t('Download')}
                                    </Button>
                                    <a
                                        style={{marginLeft: 4}}
                                        onClick={() => {
                                            openModal(
                                                t('File'),
                                                (<AppointmentFileEdit fileRecord={row} onSaved={() => {closeModal(); getFiles()}} />),
                                                () => {}
                                            );
                                        }}
                                    >
                                        <EditOutlined />
                                    </a>
                                    {
                                        !row?.isDeleted && (
                                            <Button
                                                type={'primary'}
                                                size={'small'}
                                                danger={true}
                                                onClick={async () => {
                                                    const res = await ApiService.Appointment.DeleteFile(appointmentId, row.id)
                                                    if (res.success) {
                                                        await getFiles()
                                                    }
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        )
                                    }
                                </div>
                            )
                        }
                    },
                ]}
            />
            <Typography.Title level={4} style={{marginTop: 48}}>
                {t('Last Files')}
            </Typography.Title>
            <Table
                size={'small'}
                pagination={false}
                dataSource={lastFiles}
                scroll={{x: 800}}
                columns={[
                    {dataIndex: 'createdDate', key: 'createdDate', title: t('Date'), render: (text) => new Date(text).toLocaleString(), width: 150},
                    {
                        dataIndex: 'originalFileName',
                        key: 'originalFileName',
                        title: t('File'),
                        render: (text, row) => (
                            <div style={{wordWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                {text} {row?.isDeleted && (<Typography.Text type={'danger'}>X</Typography.Text>)}
                            </div>
                        )
                    },
                    {
                        dataIndex: 'description',
                        key: 'description',
                        title: t('Description'),
                        width: 400,
                        render: (text) => (
                            <div style={{width: 400, wordWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                {text}
                            </div>
                        )
                    },
                    {
                        dataIndex: 'fileType',
                        key: 'fileType',
                        title: t('Type'),
                        width: 150,
                        render: (text) => (
                            <div style={{width: 150, wordWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                                {FileTypes.find(x => x.id === text)?.name}
                            </div>
                        )
                    },
                    {
                        dataIndex: '#',
                        key: '#',
                        title: '#',
                        width: 175,
                        render: (text, row) => {
                            return (
                                <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                    <Button
                                        size={'small'}
                                        onClick={async () => {
                                            const res = await ApiService.Appointment.DownloadFile(row?.appointmentId, row.id)
                                            if (res.success) {
                                                window.open(res.data, '_blank')
                                            }
                                        }}
                                    >
                                        {t('Open')}
                                    </Button>
                                    <Button
                                        size={'small'}
                                        type={'primary'}
                                        onClick={async () => {
                                            const res = await ApiService.Appointment.AddFileToAppointment(row.id, appointmentId)
                                            if(res.success){
                                                await getFiles()
                                            }
                                        }}
                                    >
                                        {t('Add')}
                                    </Button>
                                </div>
                            )
                        }
                    },
                ]}
            />
        </>
    )
}